import React from 'react';
import Modal from './Modal';
import './DashboardConflictModal.css';

/**
 * 대시보드 충돌 및 처리 모달
 * ID 충돌이나 dashboard_id 변경 시 사용자 선택을 받습니다.
 */
const DashboardConflictModal = ({ 
  isOpen, 
  onClose, 
  conflictInfo, 
  onReplaceExisting, 
  onCreateNew,
  onDeleteOriginal, // 기존 대시보드 삭제 핸들러 추가
  onKeepBoth, // 두 대시보드 모두 유지하고 새 대시보드로 이동
  saving = false 
}) => {
  if (!isOpen || !conflictInfo) return null;

  const { 
    conflictType, 
    existing_dashboard, 
    new_dashboard_id, 
    message,
    originalDashboardId,
    newDashboardId,
    hasCharts 
  } = conflictInfo;

  /**
   * 날짜 포맷팅 함수
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="dashboard-conflict-modal-backdrop" onClick={onClose}>
        <div className="dashboard-conflict-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">대시보드 ID 충돌</h3>
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={saving}
          >
            ✕
          </button>
        </div>

        <div className="modal-body">
          {conflictType === 'dashboard_id_changed' ? (
            // Dashboard ID 변경 시나리오
            <>
              <div className="conflict-message">
                새로운 대시보드가 성공적으로 생성되었습니다!
              </div>

              <div className="conflict-details">
                <div className="dashboard-info">
                  <h4>생성된 새 대시보드</h4>
                  <div className="dashboard-card new">
                    <div className="dashboard-id">ID: {newDashboardId}</div>
                    <div className="dashboard-note">
                      새로운 템플릿으로 대시보드가 생성되었습니다.
                    </div>
                  </div>
                </div>

                <div className="existing-dashboard-info">
                  <h4>기존 대시보드 처리</h4>
                  <div className="dashboard-card existing">
                    <div className="dashboard-id">ID: {originalDashboardId}</div>
                    <div className="dashboard-status">
                      {hasCharts ? (
                        <span className="has-content">차트가 포함된 대시보드</span>
                      ) : (
                        <span className="empty-dashboard">빈 대시보드</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-options">
                <div className="option-explanation">
                  <strong>다음 작업을 선택하세요:</strong>
                  <ul>
                    {hasCharts ? (
                      <>
                        <li><strong>기존 대시보드 유지:</strong> 두 대시보드를 모두 유지하고 새 대시보드로 이동합니다.</li>
                        <li><strong>기존 대시보드 삭제:</strong> 기존 대시보드를 삭제하고 새 대시보드로 이동합니다.</li>
                      </>
                    ) : (
                      <li><strong>자동 정리:</strong> 빈 대시보드는 자동으로 삭제되고 새 대시보드로 이동합니다.</li>
                    )}
                  </ul>
                </div>
              </div>
            </>
          ) : (
            // 기존 ID 충돌 시나리오
            <>
              <div className="conflict-message">
                {message}
              </div>

              <div className="conflict-details">
                <div className="existing-dashboard-info">
                  <h4>기존 대시보드</h4>
                  <div className="dashboard-card existing">
                    <div className="dashboard-card-header">
                      <div className="dashboard-id">ID: {existing_dashboard.dashboard_id}</div>
                      <div className="dashboard-updated">
                        {formatDate(existing_dashboard.updated_at)}
                      </div>
                    </div>
                    <div className="dashboard-title">{existing_dashboard.title}</div>
                  </div>
                </div>

                <div className="new-dashboard-info">
                  <h4>새로운 대시보드 ID</h4>
                  <div className="dashboard-card new">
                    <div className="dashboard-id">ID: {new_dashboard_id}</div>
                    <div className="dashboard-note">
                      이 ID로 새로운 대시보드가 생성됩니다.
                    </div>
                  </div>
                </div>
              </div>

              <div className="action-options">
                <div className="option-explanation">
                  <strong>선택 옵션:</strong>
                  <ul>
                    <li><strong>기존 대시보드 덮어쓰기:</strong> 기존 대시보드의 내용을 새로운 템플릿으로 교체합니다. (ID는 기존 것 유지)</li>
                    <li><strong>새로운 대시보드 생성:</strong> 새로운 ID로 별도의 대시보드를 생성합니다.</li>
                  </ul>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {conflictType === 'dashboard_id_changed' ? (
            // Dashboard ID 변경 시나리오 버튼들
            <>
              {hasCharts ? (
                // 차트가 있는 경우: 사용자 선택
                <>
                  <button 
                    className="btn btn-secondary" 
                    onClick={onClose}
                    disabled={saving}
                  >
                    취소
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={onKeepBoth}
                    disabled={saving}
                  >
                    {saving ? '처리 중...' : '기존 대시보드 유지하고 새 대시보드로 이동'}
                  </button>
                  <button 
                    className="btn btn-warning" 
                    onClick={onDeleteOriginal}
                    disabled={saving}
                  >
                    {saving ? '삭제 중...' : '기존 대시보드 삭제하고 새 대시보드로 이동'}
                  </button>
                </>
              ) : (
                // 빈 대시보드인 경우: 자동 처리
                <button 
                  className="btn btn-primary" 
                  onClick={onDeleteOriginal}
                  disabled={saving}
                >
                  {saving ? '처리 중...' : '확인 (빈 대시보드 자동 삭제 후 새 대시보드로 이동)'}
                </button>
              )}
            </>
          ) : (
            // 기존 ID 충돌 시나리오 버튼들
            <>
              <button 
                className="btn btn-secondary" 
                onClick={onClose}
                disabled={saving}
              >
                취소
              </button>
              <button 
                className="btn btn-warning" 
                onClick={onReplaceExisting}
                disabled={saving}
              >
                {saving ? '저장 중...' : '기존 대시보드 덮어쓰기'}
              </button>
              <button 
                className="btn btn-primary" 
                onClick={onCreateNew}
                disabled={saving}
              >
                {saving ? '생성 중...' : '새로운 대시보드 생성'}
              </button>
            </>
          )}
        </div>
      </div>
      </div>
    </Modal>
  );
};

export default DashboardConflictModal; 